import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Globe, 
  Link as LinkIcon,
  CheckCircle2,
  Loader2,
  ExternalLink,
  Trash2,
  Plus,
  Pencil,
  Save,
  X,
  Linkedin,
  Facebook
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WordPressPage() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    site_url: '',
    site_name: '',
    username: '',
    app_password: '',
    niche: ''
  });
  const [linkedinStatus, setLinkedinStatus] = useState({}); // { siteId: { personal: {connected,loading,testing}, company: {connected,loading,testing} } }
  const [facebookStatus, setFacebookStatus] = useState({}); // { siteId: { connected: bool, loading: bool, testing: bool, pages: [], selected_page_id: string } }
  const { getAuthHeaders } = useAuth();
  const { refreshSites } = useSite();

  useEffect(() => {
    fetchSites();
  }, []);

  // Check LinkedIn status for all sites when sites are loaded
  useEffect(() => {
    if (sites.length > 0) {
      sites.forEach(site => {
        checkLinkedinStatus(site.id);
        checkFacebookStatus(site.id);
      });
    }
  }, [sites.length]);

  // Check URL params for LinkedIn OAuth callback result
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('linkedin_connected') === 'true') {
      const siteId = urlParams.get('site_id');
      toast.success('LinkedIn conectat cu succes!');
      if (siteId) {
        checkLinkedinStatus(siteId);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('linkedin_error')) {
      const errorCode = urlParams.get('linkedin_error');
      const errorDetail = urlParams.get('linkedin_error_detail');

      if (errorCode === 'scope_not_authorized') {
        toast.error(
          'LinkedIn: Trebuie să activezi "Sign In with LinkedIn using OpenID Connect" în LinkedIn Developer Portal. Mergi la Apps → Products → Request Access.',
          { duration: 10000 }
        );
      } else if (errorCode === 'no_org_id') {
        toast.error(
          'LinkedIn Pagină Firmă: Completează LinkedIn Org ID în editarea site-ului întâi (secțiunea de editare, câmpul "LinkedIn Org ID").',
          { duration: 10000 }
        );
      } else if (errorDetail) {
        toast.error(`Eroare LinkedIn: ${decodeURIComponent(errorDetail.replace(/\+/g, ' '))}`, { duration: 8000 });
      } else {
        toast.error(`Eroare LinkedIn: ${errorCode}`);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('facebook_connected') === 'true') {
      const siteId = urlParams.get('site_id');
      toast.success('Facebook conectat cu succes!');
      if (siteId) {
        setFacebookStatus(prev => ({
          ...prev,
          [siteId]: { connected: true, loading: false }
        }));
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('facebook_error')) {
      toast.error(`Eroare Facebook: ${urlParams.get('facebook_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkLinkedinStatus = async (siteId) => {
    try {
      const response = await axios.get(
        `${API}/social/linkedin/status/${siteId}`,
        { headers: getAuthHeaders() }
      );
      setLinkedinStatus(prev => ({
        ...prev,
        [siteId]: {
          personal: { connected: !!response.data.personal?.connected, loading: false },
          company: { connected: !!response.data.company?.connected, loading: false }
        }
      }));
    } catch (error) {
      setLinkedinStatus(prev => ({
        ...prev,
        [siteId]: {
          personal: { connected: false, loading: false },
          company: { connected: false, loading: false }
        }
      }));
    }
  };

  const handleConnectLinkedin = async (siteId, linkedinType) => {
    const type = linkedinType || 'personal';
    setLinkedinStatus(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        [type]: { ...prev[siteId]?.[type], loading: true }
      }
    }));
    try {
      const response = await axios.get(
        `${API}/social/linkedin/auth-url/${siteId}?linkedin_type=${type}`,
        { headers: getAuthHeaders() }
      );
      window.location.href = response.data.auth_url;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Eroare la conectare LinkedIn';
      toast.error(errorMsg);
      setLinkedinStatus(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          [type]: { connected: false, loading: false }
        }
      }));
    }
  };

  const handleTestLinkedin = async (siteId, linkedinType) => {
    const type = linkedinType || 'personal';
    try {
      setLinkedinStatus(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          [type]: { ...prev[siteId]?.[type], testing: true }
        }
      }));

      const response = await axios.post(
        `${API}/social/linkedin/test-post/${siteId}?linkedin_type=${type}`,
        {},
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        toast.success('✅ Test LinkedIn reușit! Verifică profilul tău LinkedIn pentru postare.');
      } else {
        toast.error(`❌ Test eșuat: ${response.data.error || response.data.message}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Eroare la testare LinkedIn';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setLinkedinStatus(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          [type]: { ...prev[siteId]?.[type], testing: false }
        }
      }));
    }
  };

  const handleDisconnectLinkedin = async (siteId, linkedinType) => {
    const type = linkedinType || 'personal';
    try {
      await axios.delete(
        `${API}/social/linkedin/disconnect/${siteId}?linkedin_type=${type}`,
        { headers: getAuthHeaders() }
      );
      setLinkedinStatus(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          [type]: { connected: false, loading: false }
        }
      }));
      toast.success(`LinkedIn (${type === 'company' ? 'Pagină Firmă' : 'Profil Personal'}) deconectat`);
    } catch (error) {
      toast.error('Eroare la deconectare');
    }
  };

  // Facebook functions
  const checkFacebookStatus = async (siteId) => {
    try {
      const response = await axios.get(
        `${API}/social/facebook/status/${siteId}`,
        { headers: getAuthHeaders() }
      );
      setFacebookStatus(prev => ({
        ...prev,
        [siteId]: { 
          connected: response.data.connected, 
          loading: false,
          pages: response.data.pages || [],
          selected_page_id: response.data.selected_page_id
        }
      }));
    } catch (error) {
      setFacebookStatus(prev => ({
        ...prev,
        [siteId]: { connected: false, loading: false, pages: [] }
      }));
    }
  };

  const handleConnectFacebook = async (siteId) => {
    setFacebookStatus(prev => ({
      ...prev,
      [siteId]: { ...prev[siteId], loading: true }
    }));
    try {
      const response = await axios.get(
        `${API}/social/facebook/auth-url/${siteId}`,
        { headers: getAuthHeaders() }
      );
      window.location.href = response.data.auth_url;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Eroare la conectare Facebook';
      toast.error(errorMsg);
      setFacebookStatus(prev => ({
        ...prev,
        [siteId]: { connected: false, loading: false, pages: [] }
      }));
    }
  };

  const handleDisconnectFacebook = async (siteId) => {
    try {
      await axios.delete(
        `${API}/social/facebook/disconnect/${siteId}`,
        { headers: getAuthHeaders() }
      );
      setFacebookStatus(prev => ({
        ...prev,
        [siteId]: { connected: false, loading: false, pages: [] }
      }));
      toast.success('Facebook deconectat');
    } catch (error) {
      toast.error('Eroare la deconectare');
    }
  };

  const handleTestFacebook = async (siteId) => {
    try {
      setFacebookStatus(prev => ({
        ...prev,
        [siteId]: { ...prev[siteId], testing: true }
      }));

      const response = await axios.post(
        `${API}/social/facebook/test-post/${siteId}`,
        {},
        { headers: getAuthHeaders() }
      );

      toast.success('✅ ' + (response.data.message || 'Test Facebook reușit!'));
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Eroare la testare Facebook';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setFacebookStatus(prev => ({
        ...prev,
        [siteId]: { ...prev[siteId], testing: false }
      }));
    }
  };

  const handleSelectFacebookPage = async (siteId, pageId) => {
    try {
      await axios.post(
        `${API}/social/facebook/select-page/${siteId}?page_id=${pageId}`,
        {},
        { headers: getAuthHeaders() }
      );
      setFacebookStatus(prev => ({
        ...prev,
        [siteId]: { ...prev[siteId], selected_page_id: pageId }
      }));
      toast.success('Pagină Facebook selectată');
    } catch (error) {
      toast.error('Eroare la selectare pagină');
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/wordpress/sites`, { headers: getAuthHeaders() });
      setSites(response.data);
    } catch (error) {
      console.error('Failed to fetch WordPress sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    
    if (!formData.site_url || !formData.username || !formData.app_password) {
      toast.error('Completează toate câmpurile obligatorii');
      return;
    }

    setConnecting(true);
    try {
      await axios.post(`${API}/wordpress/connect`, formData, { 
        headers: getAuthHeaders() 
      });
      toast.success('Site WordPress adăugat cu succes!');
      setFormData({ site_url: '', site_name: '', username: '', app_password: '', niche: '' });
      setShowForm(false);
      fetchSites();
      refreshSites(); // Update global site list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la conectare');
    } finally {
      setConnecting(false);
    }
  };

  const handleDelete = async (siteId, siteName) => {
    if (!window.confirm(`Ești sigur că vrei să ștergi site-ul "${siteName}"?`)) return;
    
    try {
      await axios.delete(`${API}/wordpress/site/${siteId}`, { headers: getAuthHeaders() });
      toast.success('Site șters');
      fetchSites();
      refreshSites(); // Update global site list
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const startEditing = (site) => {
    setEditingSite(site.id);
    setEditForm({
      site_name: site.site_name || '',
      username: site.username || '',
      app_password: '',
      niche: site.niche || '',
      linkedin_org_id: site.linkedin_org_id || ''
    });
  };

  const cancelEditing = () => {
    setEditingSite(null);
    setEditForm({});
  };

  const handleSaveEdit = async (siteId) => {
    setSaving(true);
    try {
      const updateData = { ...editForm };
      if (!updateData.app_password) {
        delete updateData.app_password;
      }
      await axios.patch(`${API}/wordpress/site/${siteId}`, updateData, { 
        headers: getAuthHeaders() 
      });
      toast.success('Site actualizat cu succes!');
      setEditingSite(null);
      fetchSites();
      refreshSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizare');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-secondary rounded w-48" />
        <div className="h-64 bg-secondary rounded-lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="wordpress-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Site-uri WordPress</h1>
          <p className="text-muted-foreground mt-1">
            Gestionează site-urile WordPress pentru publicare automată
          </p>
        </div>
        <Button
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => setShowForm(!showForm)}
          data-testid="add-site-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Adaugă Site
        </Button>
      </div>

      {/* Add Site Form */}
      {showForm && (
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <LinkIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="font-heading text-lg">Adaugă Site WordPress</CardTitle>
                <CardDescription>
                  Introdu datele de conectare pentru noul site
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleConnect} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="site_url">URL Site WordPress *</Label>
                  <Input
                    id="site_url"
                    placeholder="https://site-ul-tau.ro"
                    value={formData.site_url}
                    onChange={(e) => setFormData({...formData, site_url: e.target.value})}
                    className="bg-secondary border-border"
                    required
                    data-testid="wordpress-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="site_name">Nume Site (opțional)</Label>
                  <Input
                    id="site_name"
                    placeholder="Ex: Blog Principal"
                    value={formData.site_name}
                    onChange={(e) => setFormData({...formData, site_name: e.target.value})}
                    className="bg-secondary border-border"
                    data-testid="wordpress-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="niche">Nișă / Industrie *</Label>
                <Input
                  id="niche"
                  placeholder="Ex: Maritime Jobs, Real Estate, Fitness"
                  value={formData.niche}
                  onChange={(e) => setFormData({...formData, niche: e.target.value})}
                  className="bg-secondary border-border"
                  required
                  data-testid="wordpress-niche-input"
                />
                <p className="text-xs text-muted-foreground">
                  Nișa va fi folosită pentru a genera backlinks și keywords relevante
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Nume Utilizator *</Label>
                  <Input
                    id="username"
                    placeholder="admin"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="bg-secondary border-border"
                    required
                    data-testid="wordpress-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app_password">Application Password *</Label>
                  <Input
                    id="app_password"
                    type="password"
                    placeholder="xxxx xxxx xxxx xxxx"
                    value={formData.app_password}
                    onChange={(e) => setFormData({...formData, app_password: e.target.value})}
                    className="bg-secondary border-border"
                    required
                    data-testid="wordpress-password-input"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={connecting}
                  data-testid="connect-wordpress-button"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Se conectează...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Conectează Site
                    </>
                  )}
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  className="border-border"
                  onClick={() => setShowForm(false)}
                >
                  Anulează
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Sites List */}
      {sites.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Globe className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Niciun site conectat</h3>
            <p className="text-muted-foreground text-center mb-6">
              Adaugă primul tău site WordPress pentru publicare automată
            </p>
            <Button 
              className="bg-primary text-primary-foreground"
              onClick={() => setShowForm(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adaugă Site
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sites.map((site) => (
            <Card key={site.id} className="bg-card border-border hover:border-primary/30 transition-colors">
              <CardContent className="pt-6">
                {editingSite === site.id ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nume Site</Label>
                        <Input
                          value={editForm.site_name}
                          onChange={(e) => setEditForm({...editForm, site_name: e.target.value})}
                          className="bg-secondary border-border"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nișă</Label>
                        <Input
                          value={editForm.niche}
                          onChange={(e) => setEditForm({...editForm, niche: e.target.value})}
                          className="bg-secondary border-border"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Utilizator WordPress</Label>
                        <Input
                          value={editForm.username}
                          onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                          className="bg-secondary border-border"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Application Password (nou)</Label>
                        <Input
                          type="password"
                          value={editForm.app_password}
                          onChange={(e) => setEditForm({...editForm, app_password: e.target.value})}
                          placeholder="Lasă gol pentru a păstra parola veche"
                          className="bg-secondary border-border"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>LinkedIn Org ID (Pagină Firmă)</Label>
                        <Input
                          value={editForm.linkedin_org_id}
                          onChange={(e) => setEditForm({...editForm, linkedin_org_id: e.target.value})}
                          placeholder="Ex: 121863996"
                          className="bg-secondary border-border"
                        />
                        <p className="text-xs text-muted-foreground">
                          ID-ul numeric al paginii de companie LinkedIn. Necesar pentru a posta ca Pagină Firmă. Găsești ID-ul în URL-ul paginii: linkedin.com/company/<strong>id</strong>/admin/
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSaveEdit(site.id)}
                        disabled={saving}
                        className="bg-primary"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Salvează
                      </Button>
                      <Button variant="outline" onClick={cancelEditing}>
                        <X className="w-4 h-4 mr-2" />
                        Anulează
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-lg bg-green-500/10">
                          <Globe className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{site.site_name || site.site_url}</h3>
                          <a
                            href={site.site_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
                          >
                            {site.site_url}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                          <p className="text-xs text-muted-foreground mt-1">
                            Utilizator: {site.username}
                          </p>
                          {site.niche && (
                            <Badge variant="outline" className="mt-2 text-xs">
                              Nișă: {site.niche}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Conectat
                      </Badge>
                    </div>

                    <div className="flex flex-col gap-2">
                      {/* Facebook */}
                      <div className="flex items-center justify-between gap-3 flex-wrap bg-secondary/40 border border-border rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 text-sm font-medium shrink-0">
                          <Facebook className="w-4 h-4 text-blue-600 shrink-0" />
                          Facebook
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          {facebookStatus[site.id]?.connected ? (
                            <>
                              <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Conectat
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleTestFacebook(site.id)}
                                disabled={facebookStatus[site.id]?.testing}
                                className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                                data-testid={`facebook-test-${site.id}`}
                              >
                                {facebookStatus[site.id]?.testing ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : 'Test'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConnectFacebook(site.id)}
                                className="text-blue-600 border-blue-600/30 hover:bg-blue-600/10"
                                data-testid={`facebook-reconnect-${site.id}`}
                                title="Reconecteaza Facebook"
                              >
                                <Facebook className="w-4 h-4 mr-1" />
                                Reconectează
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDisconnectFacebook(site.id)}
                                className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                data-testid={`facebook-disconnect-${site.id}`}
                              >
                                X
                              </Button>
                              {facebookStatus[site.id]?.pages?.length > 0 && (
                                <select
                                  value={facebookStatus[site.id]?.selected_page_id || ''}
                                  onChange={(e) => handleSelectFacebookPage(site.id, e.target.value)}
                                  className="text-xs bg-secondary border border-border rounded px-2 py-1 max-w-[150px]"
                                  data-testid={`facebook-page-select-${site.id}`}
                                >
                                  <option value="">Selectează pagina</option>
                                  {facebookStatus[site.id].pages.map(page => (
                                    <option key={page.id} value={page.id}>
                                      {page.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </>
                          ) : (
                            <>
                              <Badge variant="outline" className="text-muted-foreground border-border">
                                Neconectat
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConnectFacebook(site.id)}
                                disabled={facebookStatus[site.id]?.loading}
                                className="text-muted-foreground border-border hover:text-blue-600 hover:border-blue-600/30"
                                data-testid={`facebook-connect-${site.id}`}
                              >
                                {facebookStatus[site.id]?.loading ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : 'Conectează'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* LinkedIn - Profil Personal */}
                      <div className="flex items-center justify-between gap-3 flex-wrap bg-secondary/40 border border-border rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 text-sm font-medium shrink-0">
                          <Linkedin className="w-4 h-4 text-blue-500 shrink-0" />
                          LinkedIn <span className="text-muted-foreground font-normal">— Profil Personal</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          {linkedinStatus[site.id]?.personal?.connected ? (
                            <>
                              <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Conectat
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleTestLinkedin(site.id, 'personal')}
                                disabled={linkedinStatus[site.id]?.personal?.testing}
                                className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                                data-testid={`linkedin-personal-test-${site.id}`}
                              >
                                {linkedinStatus[site.id]?.personal?.testing ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : 'Test'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConnectLinkedin(site.id, 'personal')}
                                className="text-blue-500 border-blue-500/30 hover:bg-blue-500/10"
                                data-testid={`linkedin-personal-reconnect-${site.id}`}
                                title="Reconecteaza LinkedIn Personal"
                              >
                                <Linkedin className="w-4 h-4 mr-1" />
                                Reconectează
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDisconnectLinkedin(site.id, 'personal')}
                                className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                data-testid={`linkedin-personal-disconnect-${site.id}`}
                              >
                                X
                              </Button>
                            </>
                          ) : (
                            <>
                              <Badge variant="outline" className="text-muted-foreground border-border">
                                Neconectat
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConnectLinkedin(site.id, 'personal')}
                                disabled={linkedinStatus[site.id]?.personal?.loading}
                                className="text-muted-foreground border-border hover:text-blue-500 hover:border-blue-500/30"
                                data-testid={`linkedin-personal-connect-${site.id}`}
                              >
                                {linkedinStatus[site.id]?.personal?.loading ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : 'Conectează'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* LinkedIn - Pagină Firmă */}
                      <div className="flex items-center justify-between gap-3 flex-wrap bg-secondary/40 border border-border rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 text-sm font-medium shrink-0">
                          <Linkedin className="w-4 h-4 text-blue-500 shrink-0" />
                          LinkedIn <span className="text-muted-foreground font-normal">— Pagină Firmă</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          {linkedinStatus[site.id]?.company?.connected ? (
                            <>
                              <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Conectat
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleTestLinkedin(site.id, 'company')}
                                disabled={linkedinStatus[site.id]?.company?.testing}
                                className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                                data-testid={`linkedin-company-test-${site.id}`}
                              >
                                {linkedinStatus[site.id]?.company?.testing ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : 'Test'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConnectLinkedin(site.id, 'company')}
                                className="text-blue-500 border-blue-500/30 hover:bg-blue-500/10"
                                data-testid={`linkedin-company-reconnect-${site.id}`}
                                title="Reconecteaza LinkedIn Pagină Firmă"
                              >
                                <Linkedin className="w-4 h-4 mr-1" />
                                Reconectează
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDisconnectLinkedin(site.id, 'company')}
                                className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                data-testid={`linkedin-company-disconnect-${site.id}`}
                              >
                                X
                              </Button>
                            </>
                          ) : (
                            <>
                              <Badge variant="outline" className="text-muted-foreground border-border">
                                Neconectat
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConnectLinkedin(site.id, 'company')}
                                disabled={linkedinStatus[site.id]?.company?.loading}
                                className="text-muted-foreground border-border hover:text-blue-500 hover:border-blue-500/30"
                                data-testid={`linkedin-company-connect-${site.id}`}
                              >
                                {linkedinStatus[site.id]?.company?.loading ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : 'Conectează'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startEditing(site)}
                        className="text-muted-foreground hover:text-primary"
                        data-testid={`edit-site-${site.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(site.id, site.site_name || site.site_url)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`delete-site-${site.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Instructions */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Cum obții Application Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Conectează-te la WordPress Dashboard</li>
            <li>Navighează la <strong className="text-foreground">Users → Profile</strong></li>
            <li>Derulează până la secțiunea <strong className="text-foreground">Application Passwords</strong></li>
            <li>Introdu un nume pentru aplicație (ex: "SEO Automation")</li>
            <li>Click pe <strong className="text-foreground">Add New Application Password</strong></li>
            <li>Copiază parola generată și introdu-o aici</li>
          </ol>
          <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <p className="text-sm text-yellow-500">
              <strong>Notă:</strong> Application Passwords necesită WordPress 5.6+ și HTTPS activat pe site.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
